import { NgForOf, NgIf } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DynamicFormQuestionComponent } from '@components/dynamic-forms/dynamic-form-question/dynamic-form-question.component';
import { InputTypes } from '@enums/input-types';
import { ExternalData } from '@models/dynamic-forms/external-data';
import { Question } from '@models/dynamic-forms/question';
import { QuestionnaireAnswers } from '@models/dynamic-forms/questionnaire-answers';
import { IndexedDbItem } from '@models/indexed-db-item';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DynamicFormsService } from '@services/dynamic-forms.service';
import { IndexedDbService } from '@services/indexed-db.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dynamic-questionnaire-form',
  templateUrl: './dynamic-questionnaire-form.component.html',
  imports: [DynamicFormQuestionComponent, NgForOf, NgIf],
})
export class DynamicQuestionnaireFormComponent implements OnInit, OnDestroy {
  @Input() public indexedDbName: string;
  @Input() public storageKey: string;
  @Input() public questionIds: string[];
  @Input() public externalData: ExternalData;
  @Output() public showLabFinderModal: EventEmitter<void> = new EventEmitter();
  /** Emits once after the reactive form is constructed and wired to persistence. */
  @Output() public formReady = new EventEmitter<FormGroup>();

  public form: FormGroup;
  public questions: Question[] = [];

  private dynamicFormsService = inject(DynamicFormsService);
  private consultationStorageService = inject(ConsultationStorageService);
  private indexedDbService = inject(IndexedDbService);

  private subscriptions: Subscription[] = [];

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.resolveQuestions().then((questions) => {
      this.questions = questions;
      this.setAnswersFromStorage();
      this.form = this.dynamicFormsService.toFormGroup(this.questions);
      this.setAnswersFromIndexedDb();
      this.subscribeToFormChanges();
      this.formReady.emit(this.form);
    });
  }

  /**
   * Unsubscribes from all current subscriptions.
   */
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Resolves questions based on provided question IDs and external data. If questions are already set, it returns them.
   */
  private resolveQuestions(): Promise<Question[]> {
    if (this.questions.length) {
      return Promise.resolve(this.questions);
    }

    return this.dynamicFormsService.getQuestions(this.questionIds, this.externalData);
  }

  /**
   * Loads answers from storage and assigns them to questions.
   */
  private setAnswersFromStorage(): void {
    if (!this.storageKey) {
      return;
    }

    const answers = this.consultationStorageService.getQuestionnaireAnswers(this.storageKey);
    if (!answers) {
      return;
    }

    this.questions.forEach((question) => (question.value = answers[question.id]));
  }

  /**
   * Loads answers from IndexedDB and assigns them to form controls.
   */
  private setAnswersFromIndexedDb(): void {
    if (!this.indexedDbName) {
      return;
    }

    this.indexedDbService.getAllItems(this.indexedDbName).subscribe((answers) => {
      answers.forEach((answer: IndexedDbItem<any>) => {
        const control = this.form.get(answer.key);

        if (!control) {
          return;
        }

        control.patchValue(answer.data);
        control.updateValueAndValidity();
        control.markAsDirty();
      });

      this.subscribeToFileChanges();
    });
  }

  /**
   * Subscribes to form changes and updates stored answers.
   */
  private subscribeToFormChanges(): void {
    if (!this.storageKey) {
      return;
    }

    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) =>
        this.consultationStorageService.setQuestionnaireAnswers(this.storageKey, this.filterOutFileAnswers(value)),
      ),
    );
  }

  /**
   * Subscribes to file changes and updates stored files.
   */
  private subscribeToFileChanges(): void {
    this.getFileQuestions().forEach(({ id, metadata }) =>
      this.subscriptions.push(
        this.form.get(id).valueChanges.subscribe((file: File) => {
          if (!file) {
            this.indexedDbService.deleteItem(this.indexedDbName, id).subscribe();

            return;
          }

          this.indexedDbService.saveItem(this.indexedDbName, file, id, metadata).subscribe();
        }),
      ),
    );
  }

  /**
   * Gets the file type questions.
   *
   * @returns {Question[]} the file type questions
   */
  private getFileQuestions(): Question[] {
    return this.questions.filter(({ type }) => type === InputTypes.File);
  }

  /**
   * Filters out file type answers from the provided questionnaire answers.
   *
   * @param {QuestionnaireAnswers} answers the questionnaire answers to filter
   *
   * @returns {QuestionnaireAnswers} a new object containing answers without file type responses
   */
  private filterOutFileAnswers(answers: QuestionnaireAnswers): QuestionnaireAnswers {
    const answersCopy = { ...answers };
    this.getFileQuestions().forEach(({ id }) => delete answersCopy[id]);

    return answersCopy;
  }
}
